import React, { useEffect, useRef, useState } from 'react';
import './RightPanel.css';

// ── Donut Chart (unchanged) ─────────────────────────────────────────────────
const DonutChart = ({ data, total }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2, r = 56, innerR = 38;

    ctx.clearRect(0, 0, W, H);

    const segments = [
      { value: data.onDuty,  color: '#22c55e' },
      { value: data.offline, color: '#475569' },
    ];

    let startAngle = -Math.PI / 2;
    const totalVal = segments.reduce((s, seg) => s + seg.value, 0);
    segments.forEach(seg => {
      const sweep = (seg.value / totalVal) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, startAngle + sweep);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();
      startAngle += sweep;
    });

    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fillStyle = '#0f2035';
    ctx.fill();

    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 18px Rajdhani, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(total?.toLocaleString() || '50', cx, cy + 3);
    ctx.fillStyle = '#64748b';
    ctx.font = '9px Inter, sans-serif';
    ctx.fillText('Batons', cx, cy + 15);
  }, [data, total]);

  return <canvas ref={canvasRef} width={130} height={130} />;
};

// ── Live Activity Feed ──────────────────────────────────────────────────────
const INITIAL_FEED = [
  { id: 1, icon: '🆘', color: '#ef4444', text: 'SOS triggered — Ravi Kumar', loc: 'MG Road',        time: '10:23', type: 'sos'     },
  { id: 2, icon: '🔵', color: '#3b82f6', text: 'Patrol started — Kiran Reddy',  loc: 'Benz Circle',   time: '10:18', type: 'patrol'  },
  { id: 3, icon: '🩷', color: '#ec4899', text: 'Check-in — Sravani',        loc: 'Labbipet',      time: '10:15', type: 'checkin' },
  { id: 4, icon: '🔋', color: '#f59e0b', text: 'Low battery — VJW-0010',         loc: '15% remaining', time: '10:10', type: 'battery' },
  { id: 5, icon: '🟡', color: '#dbc052', text: 'Patrol ended — Ravi Teja',      loc: 'Governorpet',   time: '10:05', type: 'patrol'  },
  { id: 6, icon: '✅', color: '#22c55e', text: 'SOS resolved — Suresh Babu',     loc: 'Benz Circle',   time: '09:58', type: 'resolved'},
  { id: 7, icon: '🔵', color: '#3b82f6', text: 'Patrol started — Arjun Sharma', loc: 'Kanuru',        time: '09:50', type: 'patrol'  },
];

const LiveFeed = () => {
  const [feed, setFeed] = useState(INITIAL_FEED);
  const [newEntry, setNewEntry] = useState(null);

  // Simulate live updates every ~20s
  useEffect(() => {
    const updates = [
      { icon: '🔵', color: '#3b82f6', text: 'Patrol check-in — Mahesh Chandra', loc: 'Currency Nagar', type: 'checkin' },
      { icon: '🩷', color: '#ec4899', text: 'Check-in — Kavitha Naidu',          loc: 'Gunadala',       type: 'checkin' },
      { icon: '🟡', color: '#dbc052', text: 'Traffic duty — Mohan Reddy',        loc: 'MG Road Junction', type: 'patrol' },
      { icon: '🔋', color: '#f59e0b', text: 'Low battery — VJW-0025',            loc: '12% remaining',  type: 'battery' },
    ];
    let i = 0;
    const t = setInterval(() => {
      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      const entry = { ...updates[i % updates.length], id: Date.now(), time: timeStr };
      setNewEntry(entry.id);
      setFeed(prev => [entry, ...prev.slice(0, 7)]);
      setTimeout(() => setNewEntry(null), 1000);
      i++;
    }, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="live-feed">
      {feed.map(item => (
        <div
          key={item.id}
          className={`feed-item ${newEntry === item.id ? 'feed-item--new' : ''}`}
        >
          <span className="feed-dot" style={{ background: item.color }} />
          <div className="feed-body">
            <div className="feed-text">{item.text}</div>
            <div className="feed-loc">{item.loc}</div>
          </div>
          <div className="feed-time">{item.time}</div>
        </div>
      ))}
    </div>
  );
};

// ── Shift Status ────────────────────────────────────────────────────────────
const ShiftStatus = () => {
  const now = new Date();
  const hours = now.getHours();

  // Determine current shift
  let shift, shiftEnd, shiftColor;
  if (hours >= 6 && hours < 14) {
    shift = 'Morning Shift';  shiftEnd = '14:00'; shiftColor = '#f59e0b';
  } else if (hours >= 14 && hours < 22) {
    shift = 'Afternoon Shift'; shiftEnd = '22:00'; shiftColor = '#3b82f6';
  } else {
    shift = 'Night Shift';    shiftEnd = '06:00'; shiftColor = '#a855f7';
  }

  // Time remaining in shift
  let endH = parseInt(shiftEnd);
  let diffH = endH - hours;
  if (diffH < 0) diffH += 24;
  const diffM = 60 - now.getMinutes();
  const remaining = diffM === 60
    ? `${diffH}h 0m left`
    : `${diffH - 1}h ${diffM}m left`;

  // Shift progress %
  const shiftDuration = 8 * 60; // 8 hours in minutes
  const elapsedMins = ((hours % 8) * 60) + now.getMinutes();
  const progress = Math.min(Math.round((elapsedMins / shiftDuration) * 100), 100);

  return (
    <div className="shift-status">
      <div className="shift-header">
        <div>
          <div className="shift-name" style={{ color: shiftColor }}>{shift}</div>
          <div className="shift-remaining">{remaining}</div>
        </div>
        <div className="shift-pct" style={{ color: shiftColor }}>{progress}%</div>
      </div>
      <div className="shift-bar-track">
        <div
          className="shift-bar-fill"
          style={{ width: `${progress}%`, background: shiftColor }}
        />
      </div>
      <div className="shift-slots">
        {[
          { label: 'Morning',   time: '06:00–14:00', active: hours >= 6  && hours < 14,  color: '#f59e0b' },
          { label: 'Afternoon', time: '14:00–22:00', active: hours >= 14 && hours < 22,  color: '#3b82f6' },
          { label: 'Night',     time: '22:00–06:00', active: hours >= 22 || hours < 6,   color: '#a855f7' },
        ].map(s => (
          <div key={s.label} className={`shift-slot ${s.active ? 'shift-slot--active' : ''}`}
               style={s.active ? { borderColor: s.color } : {}}>
            <span className="shift-slot-dot" style={{ background: s.active ? s.color : '#334155' }} />
            <div>
              <div className="shift-slot-label" style={s.active ? { color: s.color } : {}}>{s.label}</div>
              <div className="shift-slot-time">{s.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Battery Alert Mini ──────────────────────────────────────────────────────
const BatteryAlerts = ({ data }) => {
  const lowBattery = data?.lowBattery || 3;
  const offline    = totalBatons - (data?.onDuty || 1);

  return (
    <div className="battery-alerts">
      <div className="ba-item">
        <span className="ba-icon">🔋</span>
        <div className="ba-info">
          <div className="ba-label">Low Battery Batons</div>
          <div className="ba-val" style={{ color: '#f59e0b' }}>{lowBattery} devices below 20%</div>
        </div>
      </div>
      <div className="ba-item">
        <span className="ba-icon">📵</span>
        <div className="ba-info">
          <div className="ba-label">Offline / Inactive</div>
          <div className="ba-val" style={{ color: '#a855f7' }}>{offline} batons not responding</div>
        </div>
      </div>
      <div className="ba-item">
        <span className="ba-icon">✅</span>
        <div className="ba-info">
          <div className="ba-label">System Health</div>
          <div className="ba-val" style={{ color: '#22c55e' }}>All services operational</div>
        </div>
      </div>
    </div>
  );
};

const totalBatons = 3;

// ── Main RightPanel ─────────────────────────────────────────────────────────
const RightPanel = ({ data }) => {
  const deployData = {
    onDuty:  data?.onDuty  || 1,
    offline: totalBatons - (data?.onDuty || 1),
  };

  return (
    <div className="right-panel">

      {/* 1 — Deployment Status (kept) */}
      <div className="rp-section">
        <div className="section-title">DEPLOYMENT STATUS</div>
        <div className="donut-wrap">
          <DonutChart data={deployData} total={data?.totalBatons || totalBatons} />
        </div>
        <div className="legend-list">
          <div className="legend-item">
            <span className="legend-dot-sq" style={{ background: '#22c55e' }}></span>
            <span className="legend-label">On Duty</span>
            <span className="legend-val">{deployData.onDuty}</span>
            <span className="legend-pct">(33%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot-sq" style={{ background: '#475569' }}></span>
            <span className="legend-label">Inactive / Offline</span>
            <span className="legend-val">{deployData.offline}</span>
            <span className="legend-pct">(67%)</span> 
          </div>
        </div>
      </div>





      {/* 4 — System Alerts */}
      <div className="rp-section">
        <div className="section-title">SYSTEM ALERTS</div>
        <BatteryAlerts data={data} />
      </div>

    </div>
  );
};

export default RightPanel;
